"use client";

import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { openSalePdf } from "@/components/pos/PaymentModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { resolveInvoicePortalUrl } from "@/lib/invoice-portal";

type SaleItemRow = {
  id: number;
  product_name: string;
  qty: number;
  paid_qty?: number | null;
  free_qty?: number | null;
  rate: number;
  discount_pct?: number;
  discount_amount?: number;
  offer_name?: string | null;
  offer_details?: string | null;
  amount: number;
  gst_rate?: number;
};

type SalePaymentRow = {
  id: number;
  payment_mode: string;
  amount: number;
  reference_no?: string | null;
};

type SaleDetail = {
  id: number;
  bill_no: string;
  sale_date: string;
  customer_name?: string | null;
  customer_phone?: string | null;
  net_amount: number;
  taxable_amount?: number;
  cgst_amount?: number;
  sgst_amount?: number;
  igst_amount?: number;
  public_invoice_url?: string | null;
  public_invoice_portal_url?: string | null;
  public_invoice_token?: string | null;
  items: SaleItemRow[];
  payments: SalePaymentRow[];
};

function itemPaidQty(it: SaleItemRow): number {
  if (it.paid_qty != null) {
    return Number(it.paid_qty);
  }

  return Number(it.qty);
}

function itemFreeQty(it: SaleItemRow): number {
  return Number(it.free_qty ?? 0);
}

function itemOfferLabel(it: SaleItemRow): string {
  const name = (it.offer_name ?? "").trim();
  const details = (it.offer_details ?? "").trim();
  if (name && details && name !== details) {
    return `${name} — ${details}`;
  }

  return name || details || "—";
}

export default function SaleDetailPage() {
  const params = useParams();
  const id = Number(params.id);
  const [pdfErr, setPdfErr] = useState<string | null>(null);

  const { data: sale, isLoading, isError } = useQuery({
    queryKey: ["sale", id],
    queryFn: async () => {
      const { data } = await api.get<{ sale: SaleDetail }>(`/api/sales/${id}`);
      return data.sale;
    },
    enabled: Number.isFinite(id) && id > 0,
  });

  const portalUrl = sale ? resolveInvoicePortalUrl(sale) : null;

  if (!Number.isFinite(id) || id <= 0) {
    return <p className="p-6 text-sm text-destructive">Invalid sale id</p>;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/reports/sales"
          className={cn(buttonVariants({ variant: "outline", size: "sm" }), "inline-flex items-center")}
        >
          ← Sales register
        </Link>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!sale}
            onClick={() => {
              if (!sale) return;
              setPdfErr(null);
              void openSalePdf(sale.id).catch((e: unknown) => {
                let msg = "Could not generate PDF.";
                if (e instanceof Error && e.message) {
                  msg = e.message;
                } else if (axios.isAxiosError(e)) {
                  const data = e.response?.data as { error?: string } | undefined;
                  msg = data?.error ?? msg;
                }
                setPdfErr(msg);
              });
            }}
          >
            PDF
          </Button>
          {portalUrl ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  window.open(portalUrl, "_blank");
                }}
              >
                Open portal
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(portalUrl);
                  } catch {
                    window.prompt("Copy link:", portalUrl);
                  }
                }}
              >
                Copy link
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
      {isError ? <p className="text-sm text-destructive">Failed to load bill.</p> : null}
      {pdfErr ? <p className="text-sm text-destructive">{pdfErr}</p> : null}

      {sale ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Bill <span className="font-mono">{sale.bill_no}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex flex-wrap justify-between gap-2">
                <span className="text-muted-foreground">Date</span>
                <span className="font-mono">{sale.sale_date}</span>
              </div>
              <div className="flex flex-wrap justify-between gap-2">
                <span className="text-muted-foreground">Customer</span>
                <span>
                  {sale.customer_name ?? "—"}
                  {sale.customer_phone ? ` · ${sale.customer_phone}` : ""}
                </span>
              </div>
              <div className="flex flex-wrap justify-between gap-2">
                <span className="text-muted-foreground">Net</span>
                <span className="font-mono">₹{Number(sale.net_amount ?? 0).toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Items</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr className="border-b">
                    <th className="py-2 text-left">Product</th>
                    <th className="py-2 text-right">Paid</th>
                    <th className="py-2 text-right">Free</th>
                    <th className="py-2 text-right">Bill qty</th>
                    <th className="py-2 text-right">Rate</th>
                    <th className="py-2 text-right">Disc %</th>
                    <th className="py-2 text-left">Offer</th>
                    <th className="py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(sale.items ?? []).map((it) => {
                    const paid = itemPaidQty(it);
                    const free = itemFreeQty(it);
                    const hasOffer = free > 0 || Boolean((it.offer_name ?? "").trim());

                    return (
                      <tr key={it.id} className="border-b border-border/60">
                        <td className="py-2">
                          <div>{it.product_name}</div>
                          {hasOffer && free > 0 && (
                            <p className="mt-0.5 text-[10px] text-muted-foreground">
                              {paid} paid + {free} free
                            </p>
                          )}
                        </td>
                        <td className="py-2 text-right tabular-nums">{paid.toFixed(3)}</td>
                        <td className="py-2 text-right tabular-nums">{free > 0 ? free.toFixed(3) : "—"}</td>
                        <td className="py-2 text-right tabular-nums">{Number(it.qty).toFixed(3)}</td>
                        <td className="py-2 text-right tabular-nums">{Number(it.rate).toFixed(2)}</td>
                        <td className="py-2 text-right tabular-nums">
                          {Number(it.discount_pct ?? 0).toFixed(2)}
                        </td>
                        <td className="py-2 max-w-[160px]">
                          {hasOffer ? (
                            <Badge variant="secondary" className="text-[10px] font-normal whitespace-normal">
                              {itemOfferLabel(it)}
                            </Badge>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="py-2 text-right tabular-nums">{Number(it.amount).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                  {(sale.items ?? []).length === 0 ? (
                    <tr>
                      <td className="py-3 text-sm text-muted-foreground" colSpan={8}>
                        No items.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Payments</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {(sale.payments ?? []).map((p) => (
                <div key={p.id} className="flex flex-wrap justify-between gap-2">
                  <span className="text-muted-foreground">{p.payment_mode}</span>
                  <span className="font-mono">₹{Number(p.amount).toFixed(2)}</span>
                </div>
              ))}
              {(sale.payments ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No payments recorded.</p>
              ) : null}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
